/**
 * Google Search Console Adapter (Story 4.7)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls and delegates discovery to
 * the existing discoverSearchConsoleSites() function.
 *
 * ADR-013: Adapter Pattern for API Isolation
 * ADR-4.7-A: Five Queries (now parallel via Promise.all for Worker timeout safety)
 * ADR-4.7-B: Use encodeURIComponent() for Site URL in API Path
 * ADR-4.7-C: End Date = Today - 3 Days for Initial Sync
 * ADR-4.7-E: Per-Call Timeout (5s per query, parallel execution)
 *
 * OAuth is NOT part of this adapter — handled by Story 4.5.
 * Token refresh is NOT implemented — deferred to Story 4.12+.
 * KV caching is NOT implemented — deferred to Story 4.11.
 */

import type {
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  DimensionDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { discoverSearchConsoleSites } from "./discovery";
import { GOOGLE_INTEGRATION_SCOPES } from "./scopes";
import { API_VERSIONS } from "../config";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "google-search-console" as const;

const GSC_API_BASE = `https://searchconsole.googleapis.com/webmasters/${API_VERSIONS.gsc}`;

/** Per-call timeout for GSC queries (ADR-4.7-E) */
const QUERY_TIMEOUT_MS = 5_000;

/**
 * Search types GSC supports (IMC-27 Task 4).
 * `discover` and `googleNews` do NOT support the `query` dimension, so we
 * skip topQueries for those types (see SEARCH_TYPE_SUPPORTS_QUERY).
 */
const SEARCH_TYPES = ["web", "image", "video", "news", "discover", "googleNews"] as const;
type SearchType = (typeof SEARCH_TYPES)[number];

/**
 * Search types that support the `query` dimension.
 * Marked with `satisfies` to force the compiler to surface any new search
 * type added to SEARCH_TYPES as a missing key here.
 */
const SEARCH_TYPE_SUPPORTS_QUERY = {
  web: true,
  image: true,
  video: true,
  news: true,
  discover: false,  // Discover has no query dimension (no explicit search term)
  googleNews: false, // googleNews aggregates at publisher level, no queries
} as const satisfies Record<SearchType, boolean>;

// ============================================================================
// GSC API Response Types (local to adapter, NOT exported)
// ============================================================================

interface GSCQueryResponse {
  rows?: GSCRow[];
  responseAggregationType?: string;
}

interface GSCRow {
  keys?: string[];
  clicks?: number;
  impressions?: number;
  ctr?: number;
  position?: number;
}

// ============================================================================
// Permission Filtering
// ============================================================================

/**
 * Documented catalog-contract deviation (Story 31.17 AC#4): the catalog is
 * the attachment ALLOWLIST, so "full catalog" = every ATTACHABLE resource.
 * `siteRestrictedUser`/`siteUnverifiedUser` sites cannot serve full
 * search-analytics fetches and are intentionally excluded.
 */
const ALLOWED_PERMISSIONS = new Set(["siteOwner", "siteFullUser"]);

// ============================================================================
// Numeric Value Parsing (Failure 4 prevention)
// ============================================================================

/**
 * Safely parse GSC numeric fields.
 * Uses Number.isFinite() to reject NaN, Infinity, and -Infinity.
 */
function parseNumericField(
  value: unknown,
  type: "int" | "float"
): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (value === undefined || value === null) return 0;
  const parsed =
    type === "int" ? parseInt(String(value), 10) : parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

// ============================================================================
// Country Code Validation
// ============================================================================

const COUNTRY_CODE_REGEX = /^[A-Z]{3}$/;

function validateCountryCode(code: unknown): string {
  if (typeof code === "string" && COUNTRY_CODE_REGEX.test(code)) {
    return code;
  }
  return "Unknown";
}

// ============================================================================
// JSON Parse Helper (Failure 3 prevention)
// ============================================================================

/**
 * Safely parse a GSC query response from a fetch Response.
 * Consumes the response body EXACTLY ONCE. `queryLabel` is any human-readable
 * identifier (typically the report type) embedded in parse-error messages for
 * debuggability.
 */
async function parseGSCResponse(
  response: Response,
  queryLabel: string
): Promise<AdapterResult<GSCQueryResponse>> {
  try {
    return adapterOk((await response.json()) as GSCQueryResponse);
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: sanitizeErrorMessage(
        `Failed to parse GSC ${queryLabel} response: ${err instanceof Error ? err.message : String(err)}`
      ),
      retryable: false,
    });
  }
}

// ============================================================================
// Query Builder
// ============================================================================

type GSCReportType =
  | "overview"
  | "topQueries"
  | "topPages"
  | "devices"
  | "geography"
  | "searchAppearance"
  | "hourly";

interface GSCQueryConfig {
  /** Report type emitted on resulting records */
  reportType: GSCReportType;
  /** Dimensions requested (omit for aggregate overview) */
  dimensions?: string[];
  /** Max rows to request */
  rowLimit: number;
  /** Search type this query is scoped to (IMC-27 Task 4) */
  searchType: SearchType;
  /**
   * dataState to request ("final" for stable data; "HOURLY_ALL" required for
   * HOUR dimension per GSC API docs).
   */
  dataState: "final" | "HOURLY_ALL";
}

/**
 * Build the full set of GSC queries to execute. For each supported search type,
 * emit overview + topPages + devices + geography. topQueries is emitted ONLY
 * for types that support the `query` dimension (web/image/video/news). Adds:
 *   - searchAppearance (web only — requires standalone query, no dim mixing)
 *   - hourly (web only — requires dataState: HOURLY_ALL)
 */
function buildAllQueryConfigs(): GSCQueryConfig[] {
  const configs: GSCQueryConfig[] = [];

  for (const searchType of SEARCH_TYPES) {
    // Overview: always present
    configs.push({ reportType: "overview", rowLimit: 1, searchType, dataState: "final" });

    // topQueries: only where query dimension is supported
    if (SEARCH_TYPE_SUPPORTS_QUERY[searchType]) {
      configs.push({
        reportType: "topQueries",
        dimensions: ["query"],
        rowLimit: 10,
        searchType,
        dataState: "final",
      });
    }

    configs.push({ reportType: "topPages", dimensions: ["page"], rowLimit: 10, searchType, dataState: "final" });
    configs.push({ reportType: "devices", dimensions: ["device"], rowLimit: 5, searchType, dataState: "final" });
    configs.push({ reportType: "geography", dimensions: ["country"], rowLimit: 10, searchType, dataState: "final" });
  }

  // searchAppearance — standalone query. Per GSC docs, searchAppearance
  // dimension CANNOT be combined with query/page/country/device. Web type only.
  configs.push({
    reportType: "searchAppearance",
    dimensions: ["searchAppearance"],
    rowLimit: 25,
    searchType: "web",
    dataState: "final",
  });

  // Hourly — requires dataState HOURLY_ALL. Web type only. Max date span the
  // API accepts for hourly data is 10 days; we constrain downstream.
  configs.push({
    reportType: "hourly",
    dimensions: ["HOUR"],
    rowLimit: 250,
    searchType: "web",
    dataState: "HOURLY_ALL",
  });

  return configs;
}

function buildQueryBody(
  config: GSCQueryConfig,
  startDate: string,
  endDate: string
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    startDate,
    endDate,
    type: config.searchType,
    rowLimit: config.rowLimit,
    dataState: config.dataState,
  };
  if (config.dimensions) {
    body.dimensions = config.dimensions;
  }
  return body;
}

// ============================================================================
// Response Transformation
// ============================================================================

function transformOverviewRow(
  row: GSCRow,
  searchType: SearchType
): Record<string, unknown> {
  return {
    _reportType: "overview",
    search_type: searchType,
    clicks: parseNumericField(row.clicks, "int"),
    impressions: parseNumericField(row.impressions, "int"),
    ctr: parseNumericField(row.ctr, "float"),
    position: parseNumericField(row.position, "float"),
  };
}

function transformDimensionRow(
  row: GSCRow,
  reportType: GSCReportType,
  searchType: SearchType
): Record<string, unknown> | null {
  const key = row.keys?.[0];
  if (key === undefined || key === null) return null;

  const base: Record<string, unknown> = {
    _reportType: reportType,
    search_type: searchType,
    clicks: parseNumericField(row.clicks, "int"),
    impressions: parseNumericField(row.impressions, "int"),
  };

  switch (reportType) {
    case "topQueries":
      base.query = key;
      base.ctr = parseNumericField(row.ctr, "float");
      base.position = parseNumericField(row.position, "float");
      break;
    case "topPages":
      base.page = key;
      base.ctr = parseNumericField(row.ctr, "float");
      base.position = parseNumericField(row.position, "float");
      break;
    case "devices":
      base.device = key;
      break;
    case "geography":
      base.country = validateCountryCode(key);
      break;
    case "searchAppearance":
      // Google returns opaque labels like AMP_NON_RICH_RESULTS, FAQ_RICH_RESULTS.
      // We expose them verbatim so downstream UI can display them.
      base.searchAppearance = key;
      base.ctr = parseNumericField(row.ctr, "float");
      base.position = parseNumericField(row.position, "float");
      break;
    case "hourly":
      // GSC returns the hour as ISO-8601 with timezone (e.g. "2025-04-07T14:00:00-07:00").
      // Store the raw value plus a 0-23 integer for aggregation convenience.
      base.hour = key;
      base.hourOfDay = parseHourIsoToHourOfDay(key);
      base.ctr = parseNumericField(row.ctr, "float");
      base.position = parseNumericField(row.position, "float");
      break;
    default:
      break;
  }

  return base;
}

/**
 * Extract 0-23 integer hour from an ISO-8601 hour key like
 * "2025-04-07T14:00:00-07:00". Returns -1 when unparseable.
 */
function parseHourIsoToHourOfDay(iso: string): number {
  // Match "T(HH):" capturing the hour digits. Reliable even without a real
  // timezone-aware Date parse (tz offset affects the later portion only).
  const match = /T(\d{2}):/.exec(iso);
  if (!match) return -1;
  const hr = parseInt(match[1]!, 10);
  return Number.isFinite(hr) && hr >= 0 && hr <= 23 ? hr : -1;
}

/** Max days of hourly GSC data per API constraint (announced April 2025). */
const HOURLY_MAX_DAYS = 10;

/**
 * The GSC hourly endpoint rejects ranges wider than ~10 days. When callers ask
 * for a 30-day report, we keep the END date pinned to the requested end and
 * move the START forward so the hourly subset fits in the allowed window.
 * Returns an ISO date string (YYYY-MM-DD).
 */
function clampHourlyStartDate(startDateStr: string, endDateStr: string): string {
  const start = new Date(`${startDateStr}T00:00:00Z`);
  const end = new Date(`${endDateStr}T00:00:00Z`);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
    return startDateStr;
  }
  const diffDays = Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays <= HOURLY_MAX_DAYS) return startDateStr;
  const clamped = new Date(end.getTime() - HOURLY_MAX_DAYS * 24 * 60 * 60 * 1000);
  return clamped.toISOString().split("T")[0]!;
}

/**
 * Log a secondary-query failure (non-web search type, searchAppearance, hourly).
 * Secondary failures don't block the main adapter response — we record them
 * for observability without failing the tenant-facing fetch.
 *
 * @param ctx - adapter context (integrationId/tenantId pass through to logs)
 * @param config - query config that failed (report type + search type)
 * @param error - adapter error surface; only `code` is logged (message kept
 *   out of the log to avoid provider-message bloat per log line)
 */
function logSecondaryFailure(
  ctx: AdapterContext,
  config: GSCQueryConfig,
  error: { code: string; message: string }
): void {
  console.log(
    JSON.stringify({
      event: "gsc_secondary_query_failed",
      integrationId: ctx.integrationId,
      tenantId: ctx.tenantId,
      reportType: config.reportType,
      searchType: config.searchType,
      errorCode: error.code,
      // Intentionally omit error.message — may contain provider context we
      // don't want in every log line. The code is enough for debugging.
      timestamp: new Date().toISOString(),
    })
  );
}

function transformQueryResponse(
  response: GSCQueryResponse,
  config: GSCQueryConfig
): Record<string, unknown>[] {
  const rows = response.rows ?? [];
  const records: Record<string, unknown>[] = [];

  for (const row of rows) {
    if (config.reportType === "overview") {
      records.push(transformOverviewRow(row, config.searchType));
    } else {
      const record = transformDimensionRow(row, config.reportType, config.searchType);
      if (record) {
        records.push(record);
      }
    }
  }

  return records;
}

// ============================================================================
// Available Metrics Definition
// ============================================================================

const GSC_METRICS: MetricDefinition[] = [
  { key: "clicks", label: "Clicks", type: "number", category: "search", dataType: "Integer", aggregationKind: "additive" },
  { key: "impressions", label: "Impressions", type: "number", category: "search", dataType: "Integer", aggregationKind: "additive" },
  { key: "ctr", label: "CTR", type: "percentage", category: "search", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "clicks", denominator: "impressions" } },
  { key: "position", label: "Avg. Position", type: "number", category: "search", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" } },
];

// ============================================================================
// Available Dimensions Definition (Story 5.34 + IMC-27)
// ============================================================================

const GSC_DIMENSIONS: DimensionDefinition[] = [
  { key: "date", label: "Date", type: "Time", exampleValues: ["2026-03-01"] },
  { key: "query", label: "Search Query", type: "Search", exampleValues: ["marketing analytics", "seo tools"] },
  { key: "page", label: "Page URL", type: "Content", exampleValues: ["https://example.com/", "https://example.com/blog"] },
  { key: "device", label: "Device", type: "Tech", exampleValues: ["DESKTOP", "MOBILE", "TABLET"] },
  { key: "country", label: "Country", type: "Geo", exampleValues: ["USA", "GBR", "CAN"] },
  // IMC-27: searchAppearance cannot combine with other dimensions — standalone query only.
  { key: "searchAppearance", label: "Search Appearance", type: "Search", exampleValues: ["AMP_NON_RICH_RESULTS", "FAQ_RICH_RESULTS", "REVIEW_RICH_RESULTS"] },
  // IMC-27: hour dimension — requires dataState HOURLY_ALL (Apr 2025+).
  { key: "hour", label: "Hour (ISO-8601)", type: "Time", exampleValues: ["2026-04-07T14:00:00-07:00"] },
  // IMC-27: derived hour-of-day (0-23) for convenient time-of-day grouping.
  // Computed locally by the adapter from `hour` (ISO key) — not a GSC API field.
  { key: "hourOfDay", label: "Hour of Day (0-23)", type: "Time", exampleValues: ["0", "12", "23"] },
  // IMC-27: search_type filter — one of web/image/video/news/discover/googleNews.
  // Populated on every record from the query's `type` parameter.
  { key: "search_type", label: "Search Type", type: "Search", exampleValues: ["web", "image", "video", "news", "discover", "googleNews"] },
];

// ============================================================================
// GSC Adapter (IntegrationAdapter implementation)
// ============================================================================

/**
 * GSC adapter — plain object implementing IntegrationAdapter.
 * Typed explicitly as IntegrationAdapter (no `as` casts).
 *
 * NOTE: Token refresh is NOT implemented in this story (deferred to Story 4.12+).
 */
export const gscAdapter: IntegrationAdapter = {
  platform: PLATFORM,

  rateLimits: {
    requestsPerMinute: 200,
    requestsPerDay: 100_000,
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const accountId = ctx.accountId;

    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message:
          "No Search Console site provided. Please select a property first.",
        retryable: false,
      });
    }

    // Convert Date objects to YYYY-MM-DD strings
    const startDateStr = dateRange.start.toISOString().split("T")[0];
    const endDateStr = dateRange.end.toISOString().split("T")[0];

    if (!startDateStr || !endDateStr) {
      return adapterErr({
        code: "invalid_response",
        message: "Invalid date range: could not format dates",
        retryable: false,
      });
    }

    // Encode the siteUrl for the API path (ADR-4.7-B)
    const encodedSiteUrl = encodeURIComponent(accountId);
    const queryUrl = `${GSC_API_BASE}/sites/${encodedSiteUrl}/searchAnalytics/query`;

    // Execute all queries in parallel to avoid Worker timeout (ADR perf fix)
    const fetchHeaders = {
      Authorization: `Bearer ${credentials.accessToken}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    // Build per-config queries, including hourly (date-range-clamped to 10 days)
    // and all 6 search types (IMC-27 Task 4).
    const allConfigs = buildAllQueryConfigs();

    // Compute the effective hourly start date once so we can surface it in logs
    // if it differs from the requested range (API constraint: max 10 days).
    const hourlyStart = clampHourlyStartDate(startDateStr, endDateStr);
    if (hourlyStart !== startDateStr) {
      console.log(
        JSON.stringify({
          event: "gsc_hourly_date_range_clamped",
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          requestedStart: startDateStr,
          effectiveStart: hourlyStart,
          endDate: endDateStr,
          reason: "GSC hourly endpoint accepts max 10 days of data",
          timestamp: new Date().toISOString(),
        })
      );
    }

    const rawResults = await Promise.all(
      allConfigs.map((config) => {
        const effectiveStart = config.dataState === "HOURLY_ALL"
          ? hourlyStart
          : startDateStr;
        const body = buildQueryBody(config, effectiveStart, endDateStr);
        return safeFetch(
          queryUrl,
          { method: "POST", headers: fetchHeaders, body: JSON.stringify(body) },
          QUERY_TIMEOUT_MS
        );
      })
    );

    // Validate results and transform (order preserved by Promise.all).
    // IMC-27 Task 4: The original primary query (web + overview) MUST succeed
    // — treat failures as adapter-level errors (auth, site missing, etc.).
    // Other queries (e.g. discover, googleNews, searchAppearance, hourly) can
    // fail silently — some search types may be unavailable for a given site.
    const allRecords: Record<string, unknown>[] = [];
    let transientFailures = 0;

    for (let i = 0; i < rawResults.length; i++) {
      const result = rawResults[i]!;
      const config = allConfigs[i]!;
      const isPrimary = config.searchType === "web" && config.reportType === "overview";

      if (!result.success) {
        // Fail-fast on auth errors — they apply to EVERY query, no point continuing.
        if (
          result.error.code === "token_expired" ||
          result.error.code === "token_invalid" ||
          result.error.code === "permission_denied" ||
          result.error.code === "account_not_found"
        ) {
          return adapterErr(result.error);
        }
        // Primary (web overview) must succeed — fail the fetch if it doesn't.
        if (isPrimary) {
          return adapterErr(result.error);
        }
        // Secondary (other search types, searchAppearance, hourly) — log + skip.
        transientFailures += 1;
        logSecondaryFailure(ctx, config, result.error);
        continue;
      }

      // Parse JSON response EXACTLY ONCE per Response
      const parseResult = await parseGSCResponse(result.data, config.reportType);
      if (!parseResult.success) {
        if (isPrimary) return adapterErr(parseResult.error);
        transientFailures += 1;
        logSecondaryFailure(ctx, config, parseResult.error);
        continue;
      }

      const records = transformQueryResponse(parseResult.data, config);
      allRecords.push(...records);
    }

    console.log(
      JSON.stringify({
        event: "gsc_fetch_complete",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        queriesExecuted: allConfigs.length,
        queriesFailed: transientFailures,
        recordCount: allRecords.length,
        timestamp: new Date().toISOString(),
      })
    );

    return adapterOk({
      platform: PLATFORM,
      accountId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records: allRecords,
      fetchedAt: new Date(),
      recordCount: allRecords.length,
    });
  },

  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const accountId = ctx.accountId;

    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message:
          "No Search Console site provided for connection validation.",
        retryable: false,
      });
    }

    // Lightweight call to GET /sites/{siteUrl} with 5-second timeout
    const encodedSiteUrl = encodeURIComponent(accountId);
    const url = `${GSC_API_BASE}/sites/${encodedSiteUrl}`;

    const result = await safeFetch(
      url,
      {
        headers: {
          Authorization: `Bearer ${credentials.accessToken}`,
          Accept: "application/json",
        },
      },
      QUERY_TIMEOUT_MS
    );

    if (!result.success) {
      return adapterErr(result.error);
    }

    // Consume response body exactly once (avoid unconsumed stream)
    try {
      await result.data.json();
    } catch {
      // Body consumption failed — site is still accessible if status was OK
    }

    return adapterOk(true);
  },

  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Delegate to existing discoverSearchConsoleSites() — DO NOT re-implement
    // Bridge signature: IntegrationAdapter(ctx, creds) → discovery(accessToken)
    const result = await discoverSearchConsoleSites(credentials.accessToken);

    if (!result.success) {
      return adapterErr(result.error);
    }

    // Per-resource scopes (Story 31.17 AC#1/#2): granted ∩ GSC-required,
    // computed ONCE per call (one token = one granted set). The granted set
    // is `credentials.scopes` — API-sourced from Google's token-exchange
    // response (granular consent means granted ≠ requested), NEVER the
    // requested-scopes constant, and sibling google-family scopes must not
    // leak onto GSC catalog rows. Missing/denied grant → [] (mapping is total).
    const requiredScopes =
      GOOGLE_INTEGRATION_SCOPES["google-search-console"]!.scopes;
    const gscScopes = (credentials.scopes ?? []).filter((scope) =>
      requiredScopes.includes(scope)
    );

    // Post-filter: only include siteOwner and siteFullUser (Failure 11 prevention)
    const filtered = result.data.filter((account) =>
      ALLOWED_PERMISSIONS.has(account.metadata?.permissionLevel as string)
    );

    // Defensive dedupe by EXACT id, first occurrence wins (Story 31.17 AC#3,
    // ADR-0004 §Anti-Patterns 4 — DB UNIQUE index is the backstop, not the
    // first line of defense). Exact-string only: `sc-domain:example.com`,
    // `https://example.com/` and `http://example.com/` are three DISTINCT
    // properties — no normalization-based merging (`id` is the identity,
    // never the cleaned display `name`).
    const dedupedById = new Map<string, AccountInfo>();
    for (const account of filtered) {
      if (!dedupedById.has(account.id)) {
        dedupedById.set(account.id, account);
      }
    }

    // Post-process: add display names and group labels (Failure 10 prevention)
    const processed = [...dedupedById.values()].map((account) => {
      const isDomain = account.id.startsWith("sc-domain:");
      const displayName = isDomain
        ? account.id.replace("sc-domain:", "")
        : account.id.replace(/^https?:\/\//, "").replace(/\/$/, "");

      return {
        ...account,
        name: displayName,
        // Fresh array per element — never a shared mutable reference
        // into the catalog upsert (31-16 precedent).
        scopes: [...gscScopes],
        metadata: {
          ...account.metadata,
          accountName: isDomain
            ? "Domain Properties"
            : "URL-Prefix Properties",
          propertyType: isDomain ? "domain" : "url-prefix",
          propertySubtitle: isDomain
            ? "Includes all subdomains and protocols"
            : "Only data matching this exact URL prefix",
        },
      };
    });

    return adapterOk(processed);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return GSC_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return GSC_DIMENSIONS;
  },
};
